import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  FaPlus, FaEdit, FaTrash, FaStore, FaCrown, FaAward, FaShieldAlt, FaCheck, 
  FaEye, FaUsers, FaUpload, FaPhone, FaEnvelope, FaGlobe, FaMapMarkerAlt,
  FaBuilding, FaImage
} from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';
import { Link } from 'react-router-dom';

export default function AdminShops() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [shops, setShops] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [filter, setFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form data with all fields
  const [formData, setFormData] = useState({
    name: '',
    description_fr: '',
    description_en: '',
    owner_id: '',
    logo: '',
    subtitle: '',
    address: '',
    postal_code: '',
    city: '',
    phone: '',
    email: '',
    website: '',
    siret: '',
    tva_number: '',
    position: 999
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [shopsRes, usersRes] = await Promise.all([
          axios.get(`${API}/shops?include_subscription=true`),
          axios.get(`${API}/users`),
        ]);
        setShops(shopsRes.data);
        setSuppliers(usersRes.data.filter((u) => 
          u.role === 'supplier' || 
          (u.roles && (u.roles.includes('supplier') || u.roles.includes('fournisseur')))
        ));
      } catch (error) {
        console.error('Error loading shops or suppliers:', error);
      }
    };
    load();
  }, [API]);

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops?include_subscription=true`);
      setShops(response.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, GIF ou WEBP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop grande. Maximum 5MB');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, logo: response.data.url }));
      toast.success('Logo uploadé !');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean empty strings to null for optional fields
      const cleanedData = { ...formData };
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '') cleanedData[key] = null;
      });
      cleanedData.name = formData.name; // Name is required
      cleanedData.owner_id = formData.owner_id;
      cleanedData.position = formData.position || 999;

      if (editingShop) {
        await axios.put(`${API}/shops/${editingShop.id}`, cleanedData);
        toast.success('Boutique mise à jour !');
      } else {
        await axios.post(`${API}/shops`, cleanedData);
        toast.success('Boutique créée !');
      }
      setDialogOpen(false);
      resetForm();
      fetchShops();
    } catch (error) {
      console.error('Error saving shop:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const resetForm = () => {
    setEditingShop(null);
    setFormData({
      name: '',
      description_fr: '',
      description_en: '',
      owner_id: '',
      logo: '',
      subtitle: '',
      address: '',
      postal_code: '',
      city: '',
      phone: '',
      email: '',
      website: '',
      siret: '',
      tva_number: '',
      position: 999
    });
  };

  const handleEdit = (shop) => {
    setEditingShop(shop);
    setFormData({
      name: shop.name || '',
      description_fr: shop.description_fr || '',
      description_en: shop.description_en || '',
      owner_id: shop.owner_id || '',
      logo: shop.logo || '',
      subtitle: shop.subtitle || '',
      address: shop.address || '',
      postal_code: shop.postal_code || '',
      city: shop.city || '',
      phone: shop.phone || '',
      email: shop.email || '',
      website: shop.website || '',
      siret: shop.siret || '',
      tva_number: shop.tva_number || '',
      position: shop.position || 999
    });
    setDialogOpen(true);
  };

  const handleDelete = async (shopId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette boutique et tous ses produits ?')) {
      return;
    }
    try {
      await axios.delete(`${API}/shops/${shopId}`);
      toast.success('Boutique supprimée');
      fetchShops();
    } catch (error) {
      console.error('Error deleting shop:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePositionChange = async (shopId, newPosition) => {
    try {
      await axios.patch(`${API}/shops/${shopId}/position?position=${newPosition}`);
      setShops(prevShops => 
        prevShops.map(s => 
          s.id === shopId ? { ...s, position: newPosition } : s
        ).sort((a, b) => (a.position || 999) - (b.position || 999))
      );
      toast.success(`Position mise à jour`);
    } catch (error) {
      console.error('Error updating position:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDialogClose = (open) => {
    if (!open) {
      resetForm();
    }
    setDialogOpen(open);
  };

  // Get plan badge
  const getPlanBadge = (subscription) => {
    if (!subscription) {
      return <Badge variant="outline" className="text-gray-400 border-gray-300 text-xs">Aucun</Badge>;
    }
    
    const planConfig = {
      sur_mesure: { label: 'Premium+', icon: FaCrown, className: 'bg-gradient-to-r from-amber-500 to-yellow-400 text-white border-0' },
      fournisseur: { label: 'Fournisseur', icon: FaAward, className: 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0' },
      pro: { label: 'Pro', icon: FaShieldAlt, className: 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white border-0' },
      decouverte: { label: 'Découverte', icon: FaCheck, className: 'bg-gray-100 text-gray-600' }
    };
    
    const plan = subscription.plan || 'decouverte';
    const config = planConfig[plan] || planConfig.decouverte;
    const Icon = config.icon;
    const isActive = subscription.status === 'active';
    
    return (
      <div className="flex items-center gap-2">
        <Badge className={`${config.className} flex items-center gap-1 text-xs`}>
          <Icon className="text-xs" />
          {config.label}
        </Badge>
        {isActive ? (
          <span className="text-xs text-green-600 font-medium">● Actif</span>
        ) : (
          <span className="text-xs text-gray-400">○ Inactif</span>
        )}
      </div>
    );
  };

  // Filter shops
  const filteredShops = shops.filter(shop => {
    if (filter === 'all') return true;
    if (filter === 'premium') return shop.owner_subscription?.is_premium;
    if (filter === 'active') return shop.owner_subscription?.status === 'active';
    if (filter === 'inactive') return !shop.owner_subscription || shop.owner_subscription?.status !== 'active';
    return true;
  });

  // Stats
  const stats = {
    total: shops.length,
    premium: shops.filter(s => s.owner_subscription?.is_premium).length,
    active: shops.filter(s => s.owner_subscription?.status === 'active').length,
    inactive: shops.filter(s => !s.owner_subscription || s.owner_subscription?.status !== 'active').length
  };

  // Get logo URL
  const getLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    if (logo.startsWith('/api')) return `${API.replace('/api', '')}${logo}`;
    return `${API}${logo}`;
  };

  return (
    <AdminLayout>
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-shops">
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2540]">🏪 Boutiques / Fournisseurs</h1>
            <p className="text-gray-600 text-sm mt-1">Gérez les fiches fournisseurs avec photos et descriptions</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button className="bg-[#635BFF] text-sm" data-testid="add-shop-btn">
                <FaPlus className="mr-2" /> Nouvelle boutique
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FaStore className="text-[#635BFF]" />
                  {editingShop ? 'Modifier la fiche fournisseur' : 'Nouvelle fiche fournisseur'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <Tabs defaultValue="general" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="general">Général</TabsTrigger>
                    <TabsTrigger value="contact">Contact</TabsTrigger>
                    <TabsTrigger value="legal">Légal</TabsTrigger>
                  </TabsList>
                  
                  {/* General Tab */}
                  <TabsContent value="general" className="space-y-4 mt-4">
                    {/* Logo Upload */}
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <Label className="text-sm font-medium mb-2 block">Logo</Label>
                        <div 
                          className={`w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:border-[#635BFF] transition ${formData.logo ? 'border-[#635BFF] bg-[#635BFF]/5' : 'border-gray-300'}`}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {formData.logo ? (
                            <img 
                              src={getLogoUrl(formData.logo)} 
                              alt="Logo" 
                              className="w-full h-full object-contain rounded-lg"
                            />
                          ) : (
                            <div className="text-center text-gray-400">
                              {uploading ? (
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#635BFF] mx-auto"></div>
                              ) : (
                                <>
                                  <FaImage className="text-3xl mx-auto mb-2" />
                                  <span className="text-xs">Cliquez pour ajouter</span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <input 
                          ref={fileInputRef}
                          type="file" 
                          accept="image/*" 
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        {formData.logo && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            className="mt-2 text-red-500 text-xs w-full"
                            onClick={() => setFormData(prev => ({ ...prev, logo: '' }))}
                          >
                            Supprimer le logo
                          </Button>
                        )}
                      </div>
                      
                      <div className="flex-1 space-y-4">
                        <div>
                          <Label>Nom de la boutique *</Label>
                          <Input 
                            required 
                            value={formData.name} 
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="Ex: GeoTech Solutions"
                          />
                        </div>
                        
                        <div>
                          <Label>Slogan / Sous-titre</Label>
                          <Input 
                            value={formData.subtitle} 
                            onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                            placeholder="Ex: Expert en équipements géotechniques depuis 2010"
                          />
                        </div>
                        
                        <div>
                          <Label>Propriétaire / Fournisseur *</Label>
                          <Select value={formData.owner_id} onValueChange={(value) => setFormData({ ...formData, owner_id: value })}>
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un fournisseur" />
                            </SelectTrigger>
                            <SelectContent>
                              {suppliers.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                  {s.name} ({s.email})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Description FR */}
                    <div>
                      <Label>Description (Français) *</Label>
                      <textarea
                        required
                        rows={4}
                        value={formData.description_fr}
                        onChange={(e) => setFormData({...formData, description_fr: e.target.value})}
                        className="w-full px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#635BFF]"
                        placeholder="Décrivez l'entreprise, ses spécialités, son expertise..."
                      />
                      <p className="text-xs text-gray-500 mt-1">{formData.description_fr.length}/500 caractères</p>
                    </div>
                    
                    {/* Description EN */}
                    <div>
                      <Label>Description (English)</Label>
                      <textarea
                        rows={3}
                        value={formData.description_en}
                        onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                        className="w-full px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#635BFF]"
                        placeholder="Describe the company, specialties, expertise..."
                      />
                    </div>
                    
                    {/* Position */}
                    <div className="w-32">
                      <Label>Position (ordre)</Label>
                      <Input 
                        type="number" 
                        min="1" 
                        value={formData.position} 
                        onChange={(e) => setFormData({...formData, position: parseInt(e.target.value) || 999})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Plus petit = affiché en premier</p>
                    </div>
                  </TabsContent>
                  
                  {/* Contact Tab */}
                  <TabsContent value="contact" className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="flex items-center gap-2">
                          <FaMapMarkerAlt className="text-gray-400" />
                          Adresse
                        </Label>
                        <Input 
                          value={formData.address} 
                          onChange={(e) => setFormData({...formData, address: e.target.value})}
                          placeholder="123 Rue de l'Industrie"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>Code postal</Label>
                          <Input 
                            value={formData.postal_code} 
                            onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                            placeholder="75001"
                          />
                        </div>
                        <div>
                          <Label>Ville</Label>
                          <Input 
                            value={formData.city} 
                            onChange={(e) => setFormData({...formData, city: e.target.value})}
                            placeholder="Paris"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="flex items-center gap-2">
                          <FaPhone className="text-gray-400" />
                          Téléphone
                        </Label>
                        <Input 
                          value={formData.phone} 
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          placeholder="+33 1 23 45 67 89"
                        />
                      </div>
                      
                      <div>
                        <Label className="flex items-center gap-2">
                          <FaEnvelope className="text-gray-400" />
                          Email
                        </Label>
                        <Input 
                          type="email"
                          value={formData.email} 
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="contact@entreprise.com"
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <Label className="flex items-center gap-2">
                          <FaGlobe className="text-gray-400" />
                          Site web
                        </Label>
                        <Input 
                          value={formData.website} 
                          onChange={(e) => setFormData({...formData, website: e.target.value})}
                          placeholder="https://www.entreprise.com"
                        />
                      </div>
                    </div>
                  </TabsContent>
                  
                  {/* Legal Tab */}
                  <TabsContent value="legal" className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="flex items-center gap-2">
                          <FaBuilding className="text-gray-400" />
                          SIRET
                        </Label>
                        <Input 
                          value={formData.siret} 
                          onChange={(e) => setFormData({...formData, siret: e.target.value})}
                          placeholder="123 456 789 00012"
                        />
                      </div>
                      
                      <div>
                        <Label>N° TVA Intracommunautaire</Label>
                        <Input 
                          value={formData.tva_number} 
                          onChange={(e) => setFormData({...formData, tva_number: e.target.value})}
                          placeholder="FR12345678901"
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                      💡 Ces informations apparaîtront sur les devis et factures générés pour ce fournisseur.
                    </p>
                  </TabsContent>
                </Tabs>
                
                {/* Submit buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-[#635BFF]">
                    {editingShop ? 'Mettre à jour' : 'Créer la boutique'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className={`cursor-pointer transition hover:shadow-lg ${filter === 'all' ? 'ring-2 ring-[#635BFF]' : ''}`} onClick={() => setFilter('all')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-[#0A2540]">{stats.total}</p>
                </div>
                <FaStore className="text-3xl text-gray-300" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer transition hover:shadow-lg ${filter === 'premium' ? 'ring-2 ring-amber-400' : ''}`} onClick={() => setFilter('premium')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Premium</p>
                  <p className="text-2xl font-bold text-amber-500">{stats.premium}</p>
                </div>
                <FaCrown className="text-3xl text-amber-300" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer transition hover:shadow-lg ${filter === 'active' ? 'ring-2 ring-green-400' : ''}`} onClick={() => setFilter('active')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Actifs</p>
                  <p className="text-2xl font-bold text-green-600">{stats.active}</p>
                </div>
                <FaCheck className="text-3xl text-green-300" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer transition hover:shadow-lg ${filter === 'inactive' ? 'ring-2 ring-gray-400' : ''}`} onClick={() => setFilter('inactive')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Sans abonnement</p>
                  <p className="text-2xl font-bold text-gray-500">{stats.inactive}</p>
                </div>
                <FaUsers className="text-3xl text-gray-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Shops Grid - Card View */}
        {filteredShops.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-gray-500">
              <FaStore className="text-5xl mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">Aucune boutique</p>
              <p className="text-sm mt-2">Cliquez sur "Nouvelle boutique" pour en créer une.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredShops.map((shop) => {
              const isPremium = shop.owner_subscription?.is_premium;
              return (
                <Card 
                  key={shop.id} 
                  className={`hover:shadow-xl transition-all ${isPremium ? 'ring-2 ring-amber-300 bg-amber-50/30' : ''}`}
                >
                  <CardContent className="p-5">
                    {/* Header with logo and name */}
                    <div className="flex items-start gap-4 mb-4">
                      {/* Logo */}
                      <div className={`w-16 h-16 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden ${
                        shop.logo ? '' : isPremium ? 'bg-amber-100' : 'bg-[#635BFF]/10'
                      }`}>
                        {shop.logo ? (
                          <img 
                            src={getLogoUrl(shop.logo)} 
                            alt={shop.name} 
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <FaStore className={`text-2xl ${isPremium ? 'text-amber-500' : 'text-[#635BFF]'}`} />
                        )}
                      </div>
                      
                      {/* Name and subtitle */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-[#0A2540] truncate">{shop.name}</h3>
                          {isPremium && <FaCrown className="text-amber-500 flex-shrink-0" />}
                        </div>
                        {shop.subtitle && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{shop.subtitle}</p>
                        )}
                        <div className="mt-2">
                          {getPlanBadge(shop.owner_subscription)}
                        </div>
                      </div>
                      
                      {/* Position badge */}
                      <div className="text-center flex-shrink-0">
                        <span className="text-xs text-gray-400">Pos.</span>
                        <input
                          type="number"
                          min="1"
                          value={shop.position || 999}
                          onChange={(e) => handlePositionChange(shop.id, parseInt(e.target.value) || 999)}
                          className="w-12 h-7 text-center text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#635BFF] mt-1"
                        />
                      </div>
                    </div>
                    
                    {/* Description */}
                    {shop.description_fr && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {shop.description_fr}
                      </p>
                    )}
                    
                    {/* Contact info */}
                    <div className="space-y-1 text-xs text-gray-500 mb-4">
                      {shop.city && (
                        <div className="flex items-center gap-2">
                          <FaMapMarkerAlt className="text-gray-400" />
                          <span>{[shop.address, shop.postal_code, shop.city].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                      {shop.phone && (
                        <div className="flex items-center gap-2">
                          <FaPhone className="text-gray-400" />
                          <span>{shop.phone}</span>
                        </div>
                      )}
                      {shop.email && (
                        <div className="flex items-center gap-2">
                          <FaEnvelope className="text-gray-400" />
                          <span className="truncate">{shop.email}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Owner info */}
                    {shop.owner_info && (
                      <div className="bg-gray-50 rounded-lg p-2 mb-4 text-xs">
                        <span className="text-gray-400">Propriétaire:</span>
                        <span className="font-medium ml-1">{shop.owner_info.name}</span>
                        <span className="text-gray-400 ml-1">({shop.owner_info.email})</span>
                      </div>
                    )}
                    
                    {/* Stats and actions */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div className="text-sm">
                        <span className="font-bold text-[#635BFF]">{shop.product_count || 0}</span>
                        <span className="text-gray-500 ml-1">produits</span>
                      </div>
                      
                      <div className="flex gap-1">
                        <Link to={`/shop?shop=${shop.id}`} target="_blank">
                          <Button size="sm" variant="outline" className="h-8 w-8 p-0" title="Voir la boutique">
                            <FaEye className="text-xs" />
                          </Button>
                        </Link>
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => handleEdit(shop)} title="Modifier">
                          <FaEdit className="text-xs" />
                        </Button>
                        <Button size="sm" variant="destructive" className="h-8 w-8 p-0" onClick={() => handleDelete(shop.id)} title="Supprimer">
                          <FaTrash className="text-xs" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <FaCrown className="text-amber-500" /> Premium+ (Sur Mesure)
          </span>
          <span className="flex items-center gap-1">
            <FaAward className="text-purple-500" /> Fournisseur
          </span>
          <span className="flex items-center gap-1">
            <FaShieldAlt className="text-blue-500" /> Pro
          </span>
          <span className="flex items-center gap-1">
            <FaCheck className="text-gray-500" /> Découverte
          </span>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
}
